// src/components/layout/BottomBar.jsx

import {
  Home,
  Map,
  Droplets,
  UserRound,
  Lock,
} from "lucide-react";
import {
  Link,
  useLocation,
} from "react-router-dom";
import { useBusinessGate } from "../../hooks/useBusinessGate";

const NAV_ITEMS = [
  {
    id: "home",
    to: "/dashboard",
    label: "Home",
    icon: Home,
    needsBiz: true,
  },
  {
    id: "journey",
    to: "/journey",
    label: "Journey",
    icon: Map,
    needsBiz: true,
  },
  {
    id: "practice",
    to: "/resources?tab=tasks",
    label: "Practice",
    icon: Droplets,
    needsBiz: true,
  },
  {
    id: "profile",
    to: "/profile",
    label: "Profile",
    icon: UserRound,
    needsBiz: false,
  },
];

function isNavItemActive(
  item,
  pathname,
  search
) {
  if (item.id === "home") {
    return (
      pathname === "/dashboard" ||
      pathname.startsWith(
        "/dashboard/"
      )
    );
  }

  if (item.id === "journey") {
    return (
      pathname === "/journey" ||
      pathname.startsWith(
        "/journey/"
      ) ||
      pathname.startsWith(
        "/play/"
      ) ||
      pathname.startsWith(
        "/quest/"
      )
    );
  }

  if (item.id === "practice") {
    const params =
      new URLSearchParams(
        search
      );

    const resourcesTab =
      params.get("tab");

    return (
      (
        pathname === "/resources" &&
        (
          resourcesTab === "tasks" ||
          resourcesTab === "practice"
        )
      ) ||
      pathname.startsWith(
        "/tasks"
      )
    );
  }

  if (item.id === "profile") {
    return (
      pathname === "/profile" ||
      pathname.startsWith(
        "/profile/"
      )
    );
  }

  return false;
}

export default function BottomBar() {
  const location =
    useLocation();

  const { hasBusiness } =
    useBusinessGate();

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-[#FF9F63] bg-[#FF914D]"
      aria-label="Main navigation"
    >
      <div
        className="mx-auto flex w-full max-w-[430px] items-center justify-around px-4 pt-3"
        style={{
          paddingBottom:
            "max(0.75rem, env(safe-area-inset-bottom, 0px))",
        }}
      >
        {NAV_ITEMS.map(
          (item) => {
            const Icon =
              item.icon;

            const locked =
              item.needsBiz &&
              !hasBusiness;

            const active =
              isNavItemActive(
                item,
                location.pathname,
                location.search
              );

            return (
              <Link
                key={item.id}
                to={
                  locked
                    ? "#"
                    : item.to
                }
                onClick={
                  locked
                    ? (event) =>
                        event.preventDefault()
                    : undefined
                }
                aria-label={
                  item.label
                }
                aria-current={
                  active
                    ? "page"
                    : undefined
                }
                aria-disabled={
                  locked
                }
                tabIndex={
                  locked ? -1 : 0
                }
                title={
                  locked
                    ? "Add your business details in Profile to unlock"
                    : item.label
                }
                className={[
                  "relative flex h-[52px] w-[68px] items-center justify-center rounded-2xl transition duration-150",
                  locked
                    ? "cursor-not-allowed opacity-45"
                    : "active:scale-95",
                ].join(" ")}
              >
                <div className="relative">
                  <Icon
                    className={[
                      "h-[31px] w-[31px] transition-all duration-150",
                      active
                        ? "fill-white text-white"
                        : "text-white",
                    ].join(" ")}
                    strokeWidth={
                      active
                        ? 2.7
                        : 2.3
                    }
                  />

                  {locked && (
                    <span className="absolute -right-3 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-white">
                      <Lock
                        className="h-2.5 w-2.5 text-[#FF914D]"
                        strokeWidth={3}
                      />
                    </span>
                  )}
                </div>

                <span className="sr-only">
                  {item.label}
                </span>

                {active && (
                  <span
                    className="absolute bottom-[1px] h-[3px] w-[22px] rounded-full bg-white"
                    aria-hidden="true"
                  />
                )}
              </Link>
            );
          }
        )}
      </div>
    </nav>
  );
}
